import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchUser } from './hooks';
import { API_URLS } from '../consts';
import { useHttpMutation } from '@/api';
import { ErrorLayout, useAlert } from '@/shared/components';
import { useSelector } from 'react-redux';
import { Store } from '@/redux';
import { Link, useSearchParams } from 'react-router-dom';
import { CREATE_ROOM_ROUTE } from '../create-room/route.definitions';
import { Check, X, UserPlus } from 'lucide-react';
import {
  BhabiPagination,
  InvitePlayerPageLayout,
  PaginationSlotContext,
  SearchUser,
  usePaginationHelper,
} from './components';
import { InvitePageUserAvatar } from './components/invite-page-user-avatar';
import type { SearchUserInput } from './components/search-user';

const useInvitePlayer = () =>
  useHttpMutation<unknown, { to: string[] }>(API_URLS.INVITE, 'POST');

// Chrome budget for the viewport-aware page-size calculation:
//   56px top bar + 48px tabs + ~76px search head + ~60px pagination row
//   + ~24px list padding. Safe-area insets are subtracted separately by
//   usePaginationHelper, so the page always fits above the pagination row.
const INVITE_CHROME_PX = 264;
// One user row (60px min-height) + the 8px grid gap.
const INVITE_ROW_HEIGHT = 68;

export const InviteUsers: FC = () => {
  const { onChange, userInfo, noUserFound, ...query } = useSearchUser();
  const { mutate: invite, isLoading: isInviting } = useInvitePlayer();
  const { addAlert } = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [isSelectMode, setIsSelectMode] = useState(
    searchParams.get('selectMode') === 'true'
  );

  const { isInRoom, players } = useSelector((state: Store) => ({
    ...state.userReducer,
    ...state.inPlayReducer,
  }));

  const sendInvites = (userIds: string[], name?: string) => {
    invite(
      { to: userIds },
      {
        onSuccess: () => {
          addAlert({
            type: 'success',
            message: name
              ? `Invitation sent to ${name}`
              : `Invitations sent to ${userIds.length} player(s)`,
          });
          setSelectedUsers(new Set());
          setIsSelectMode(false);
          searchParams.delete('selectMode');
          query.refetch();
        },
        onError: err => {
          addAlert({
            type: 'error',
            message: err.response?.data?.message || 'Something went wrong',
          });
        },
      }
    );
  };

  const filterJoinedPlayers = useMemo(
    () =>
      isInRoom
        ? userInfo?.filter(
            user => !players.some(player => player.id === user.id)
          )
        : userInfo,
    [players, userInfo, isInRoom]
  );

  const { page, setPage, paginatedData: paginatedPlayers, pageSize } =
    usePaginationHelper<SearchUserInput>(INVITE_CHROME_PX, INVITE_ROW_HEIGHT, filterJoinedPlayers);

  const setPaginationContent = useContext(PaginationSlotContext);
  const totalItems = filterJoinedPlayers?.length ?? 0;
  const showPagination = totalItems > pageSize;

  useEffect(() => {
    if (!setPaginationContent) return;
    setPaginationContent(
      showPagination ? (
        <BhabiPagination
          page={page}
          setPage={setPage}
          totalItems={totalItems}
          pageSize={pageSize}
        />
      ) : null
    );
    return () => setPaginationContent(null);
  }, [setPaginationContent, showPagination, page, setPage, totalItems, pageSize]);

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  // Keep selected users at the top of the current page
  const sortedPaginatedPlayers = useMemo(() => {
    if (!paginatedPlayers || selectedUsers.size === 0) return paginatedPlayers;
    return [...paginatedPlayers].sort((a, b) => {
      const aS = selectedUsers.has(a.id);
      const bS = selectedUsers.has(b.id);
      return aS === bS ? 0 : aS ? -1 : 1;
    });
  }, [paginatedPlayers, selectedUsers]);

  if (query.isError) {
    return <ErrorLayout outerBoxProps={{ height: 'auto' }} />;
  }

  return (
    <>
      <InvitePlayerPageLayout
        head={
          <div className="space-y-2.5">
            {/* Search row */}
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <SearchUser textFieldProps={{ onChange }} isLoading={query.isLoading} />
              </div>

              {isInRoom && (
                isSelectMode ? (
                  <button
                    onClick={() => {
                      setIsSelectMode(false);
                      setSelectedUsers(new Set());
                      searchParams.delete('selectMode');
                    }}
                    className="bh-btn bh-btn--ghost bh-btn--small"
                  >
                    <X size={16} aria-hidden="true" />
                    Cancel
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setIsSelectMode(true);
                      setSearchParams(prev => {
                        prev.set('selectMode', 'true');
                        return prev;
                      });
                    }}
                    className="bh-btn bh-btn--secondary bh-btn--small"
                  >
                    <Check size={16} aria-hidden="true" />
                    Select
                  </button>
                )
              )}
            </div>

            {/* Select-mode info bar */}
            {isSelectMode && isInRoom && (
              <div
                className="bh-banner bh-banner--accent"
                style={{ alignItems: 'center', justifyContent: 'space-between' }}
              >
                <p className="bh-small" style={{ margin: 0 }} aria-live="polite">
                  {selectedUsers.size > 0
                    ? `${selectedUsers.size} player${selectedUsers.size !== 1 ? 's' : ''} selected`
                    : 'Tap players to select them'}
                </p>
                {selectedUsers.size > 0 && (
                  <button
                    onClick={() => sendInvites(Array.from(selectedUsers))}
                    disabled={isInviting}
                    className="bh-btn bh-btn--primary bh-btn--small"
                    aria-busy={isInviting}
                  >
                    <UserPlus size={16} aria-hidden="true" />
                    Send invites ({selectedUsers.size})
                  </button>
                )}
              </div>
            )}

            {!isInRoom && (
              <p className="bh-banner" style={{ margin: 0 }}>
                <span aria-hidden="true">♠</span>
                <span>
                  Create a table first, then invite players to it.{' '}
                  <Link
                    to={CREATE_ROOM_ROUTE.ABSOLUTE_PATH}
                    className="bh-link"
                  >
                    Create a table
                  </Link>
                </span>
              </p>
            )}
          </div>
        }
      >
        {/* User list */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {sortedPaginatedPlayers?.map(user => (
            <InvitePageUserAvatar
              key={user.id}
              user={{
                id: user.id,
                name: user.name,
                photoURL: user.photoURL,
                firebaseUID: user.firebaseUID,
              }}
              state={user.state}
              isSelected={selectedUsers.has(user.id)}
              isSelectMode={isSelectMode}
              isInRoom={!!isInRoom}
              onSelect={toggleUserSelection}
              onClickInvite={() =>
                sendInvites([user.id], user.name)
              }
            />
          ))}
        </div>

        {noUserFound && !query.isLoading && (
          <p className="bh-muted py-8 text-center" role="status">
            No players match that search.
          </p>
        )}
      </InvitePlayerPageLayout>
    </>
  );
};
